// LI.FI API client for multi-chain USDC withdrawals
// Docs: https://docs.li.fi/api-reference/introduction
//
// All requests go through our own API proxy routes (/api/lifi/*) so the
// server-side LIFI_API_KEY is never exposed to the browser.

const LIFI_PROXY_BASE = '/api/lifi'

// ---------------------------------------------------------------------------
// Chain configurations
// ---------------------------------------------------------------------------

export interface ChainConfig {
	id: number
	name: string
	/** LI.FI chain key (e.g. "POL", "ETH", "ARB") */
	key: string
	usdc: string
	explorer: string
	chainType: 'EVM' | 'SVM'
	logoURI: string
}

/** Source chains where the user holds USDC inside the app */
export const SOURCE_CHAINS = {
	polygon: {
		id: 137,
		name: 'Polygon',
		key: 'POL',
		usdc: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', // USDC.e
		explorer: 'https://polygonscan.com',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/polygon.svg',
	},
	solana: {
		id: 1151111081099710,
		name: 'Solana',
		key: 'SOL',
		usdc: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
		explorer: 'https://solscan.io',
		chainType: 'SVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg',
	},
	bsc: {
		id: 56,
		name: 'BNB Chain',
		key: 'BSC',
		usdc: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
		explorer: 'https://bscscan.com',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/bsc.svg',
	},
} as const satisfies Record<string, ChainConfig>

/**
 * Destination chains the user can withdraw to.
 * Order here is the order displayed in the UI.
 */
export const DESTINATION_CHAINS: ChainConfig[] = [
	{
		id: 137,
		name: 'Polygon',
		key: 'POL',
		usdc: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
		explorer: 'https://polygonscan.com',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/polygon.svg',
	},
	{
		id: 1,
		name: 'Ethereum',
		key: 'ETH',
		usdc: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
		explorer: 'https://etherscan.io',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/ethereum.svg',
	},
	{
		id: 42161,
		name: 'Arbitrum',
		key: 'ARB',
		usdc: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
		explorer: 'https://arbiscan.io',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/arbitrum.svg',
	},
	{
		id: 8453,
		name: 'Base',
		key: 'BAS',
		usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
		explorer: 'https://basescan.org',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/base.svg',
	},
	{
		id: 10,
		name: 'Optimism',
		key: 'OPT',
		usdc: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
		explorer: 'https://optimistic.etherscan.io',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/optimism.svg',
	},
	{
		id: 56,
		name: 'BNB Chain',
		key: 'BSC',
		usdc: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
		explorer: 'https://bscscan.com',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/bsc.svg',
	},
	{
		id: 43114,
		name: 'Avalanche',
		key: 'AVA',
		usdc: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
		explorer: 'https://snowscan.xyz',
		chainType: 'EVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/avalanche.svg',
	},
	{
		id: 1151111081099710,
		name: 'Solana',
		key: 'SOL',
		usdc: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
		explorer: 'https://solscan.io',
		chainType: 'SVM',
		logoURI:
			'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg',
	},
]

// ---------------------------------------------------------------------------
// API types
// ---------------------------------------------------------------------------

export interface LifiToken {
	address: string
	chainId: number
	symbol: string
	decimals: number
	name: string
	coinKey?: string
	logoURI?: string
	priceUSD?: string
}

export interface LifiFeeCost {
	name: string
	description?: string
	percentage: string
	token: LifiToken
	amount: string
	amountUSD: string
	included: boolean
}

export interface LifiGasCost {
	type: string // "SEND" | "APPROVE" | "SUM"
	price?: string
	estimate?: string
	limit?: string
	amount: string
	amountUSD: string
	token: LifiToken
}

export interface LifiAction {
	fromChainId: number
	toChainId: number
	fromToken: LifiToken
	toToken: LifiToken
	fromAmount: string
	slippage?: number
	fromAddress?: string
	toAddress?: string
}

export interface LifiEstimate {
	tool: string
	fromAmount: string
	fromAmountUSD?: string
	toAmount: string
	toAmountMin: string
	toAmountUSD?: string
	approvalAddress: string
	feeCosts: LifiFeeCost[]
	gasCosts: LifiGasCost[]
	executionDuration: number
}

export interface LifiIncludedStep {
	id: string
	type: 'swap' | 'cross' | 'lifi' | 'protocol'
	tool: string
	toolDetails: { key: string; name: string; logoURI?: string }
	action: LifiAction
	estimate: LifiEstimate
}

export interface LifiTransactionRequest {
	to: string
	from: string
	data: string
	value: string
	gasLimit?: string
	gasPrice?: string
	chainId: number
}

export interface LifiQuoteResponse {
	id: string
	type: 'swap' | 'cross' | 'lifi' | 'protocol'
	tool: string
	toolDetails: { key: string; name: string; logoURI?: string }
	action: LifiAction
	estimate: LifiEstimate
	includedSteps?: LifiIncludedStep[]
	integrator?: string
	transactionRequest?: LifiTransactionRequest
}

export interface LifiStatusResponse {
	status: 'NOT_FOUND' | 'INVALID' | 'PENDING' | 'DONE' | 'FAILED'
	substatus?:
		| 'WAIT_SOURCE_CONFIRMATIONS'
		| 'WAIT_DESTINATION_TRANSACTION'
		| 'BRIDGE_NOT_AVAILABLE'
		| 'CHAIN_NOT_AVAILABLE'
		| 'REFUND_IN_PROGRESS'
		| 'UNKNOWN_ERROR'
		| 'COMPLETED'
		| 'PARTIAL'
		| 'REFUNDED'
	substatusMessage?: string
	tool: string
	sending?: {
		txHash: string
		txLink: string
		amount: string
		token: LifiToken
		chainId: number
		timestamp?: number
	}
	receiving?: {
		txHash: string
		txLink: string
		amount: string
		token: LifiToken
		chainId: number
		timestamp?: number
	}
	lifiExplorerLink?: string
	fromAddress?: string
	toAddress?: string
}

// ---------------------------------------------------------------------------
// Quote parameters
// ---------------------------------------------------------------------------

export interface LifiQuoteParams {
	fromChain: number | string
	toChain: number | string
	fromToken: string
	toToken: string
	fromAmount: string
	fromAddress: string
	toAddress?: string
	slippage?: number
	order?: 'FASTEST' | 'CHEAPEST'
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Get a LI.FI quote for a token transfer (same-chain or cross-chain).
 * Returns a Step with a ready-to-send `transactionRequest`.
 */
export async function getLifiQuote(params: LifiQuoteParams): Promise<LifiQuoteResponse> {
	const searchParams = new URLSearchParams({
		fromChain: String(params.fromChain),
		toChain: String(params.toChain),
		fromToken: params.fromToken,
		toToken: params.toToken,
		fromAmount: params.fromAmount,
		fromAddress: params.fromAddress,
		slippage: String(params.slippage ?? 0.005),
		order: params.order ?? 'CHEAPEST',
	})

	if (params.toAddress) {
		searchParams.set('toAddress', params.toAddress)
	}

	const res = await fetch(`${LIFI_PROXY_BASE}/quote?${searchParams.toString()}`, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' },
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `LI.FI API error: ${res.status}`)
	}

	return res.json()
}

/**
 * Check the status of a cross-chain transfer.
 * Pass `fromChain` to speed up the lookup.
 */
export async function getLifiStatus(
	txHash: string,
	fromChain?: number | string,
	toChain?: number | string
): Promise<LifiStatusResponse> {
	const searchParams = new URLSearchParams({ txHash })

	if (fromChain !== undefined) searchParams.set('fromChain', String(fromChain))
	if (toChain !== undefined) searchParams.set('toChain', String(toChain))

	const res = await fetch(`${LIFI_PROXY_BASE}/status?${searchParams.toString()}`, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json' },
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `LI.FI API error: ${res.status}`)
	}

	return res.json()
}

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Find a destination chain config by its numeric ID */
export function getDestinationChain(chainId: number): ChainConfig | undefined {
	return DESTINATION_CHAINS.find(c => c.id === chainId)
}

/** Get block explorer URL for a transaction hash */
export function getExplorerUrl(chainId: number, txHash: string): string {
	const chain = DESTINATION_CHAINS.find(c => c.id === chainId)
	if (!chain) return ''
	return `${chain.explorer}/tx/${txHash}`
}

/** Get the USDC address for a given chain */
export function getUsdcAddress(chainId: number): string {
	const chain = DESTINATION_CHAINS.find(c => c.id === chainId)
	if (chain) return chain.usdc
	// Also check source chains
	if (chainId === SOURCE_CHAINS.polygon.id) return SOURCE_CHAINS.polygon.usdc
	if (chainId === SOURCE_CHAINS.solana.id) return SOURCE_CHAINS.solana.usdc
	throw new Error(`Unknown chain ID: ${chainId}`)
}

/**
 * Sum up all fee costs and gas costs from a LI.FI estimate, returning the
 * total in USD.
 */
export function computeTotalFeeUsd(estimate: LifiEstimate): string {
	let total = 0
	for (const fee of estimate.feeCosts) {
		if (fee.included) continue // already deducted from fromAmount
		total += Number.parseFloat(fee.amountUSD || '0')
	}
	for (const gas of estimate.gasCosts) {
		total += Number.parseFloat(gas.amountUSD || '0')
	}
	return total.toFixed(2)
}
